import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api, MarketplaceArche, MarketplaceCategory } from '../../lib/api';
import { useCategories } from '../hooks';
import { SearchBar, ArcheGrid, CategoryNav, Pagination } from '../components';
import { useOldStoreName } from '../old-store-name';

export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const oldStore = useOldStoreName();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy = (searchParams.get('sortBy') as 'fitness' | 'popular' | 'newest') || 'fitness';

  const [category, setCategory] = useState<MarketplaceCategory | null>(null);
  const [arches, setArches] = useState<MarketplaceArche[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const { categories } = useCategories();

  useEffect(() => {
    if (!slug) return;

    const fetchCategory = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await api.getMarketplaceCategoryArches(slug, {
          sortBy,
          limit,
          offset: (page - 1) * limit,
        });
        setCategory(result.category);
        setArches(result.arches);
        setTotal(result.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load category');
        setCategory(null);
        setArches([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [slug, sortBy, page]);

  // Reset page when category or sort changes
  useEffect(() => {
    setPage(1);
  }, [slug, sortBy]);

  const handleSortChange = (newSortBy: string) => {
    const params = new URLSearchParams(searchParams);
    if (newSortBy) {
      params.set('sortBy', newSortBy);
    } else {
      params.delete('sortBy');
    }
    setSearchParams(params);
  };

  const totalPages = Math.ceil(total / limit);

  if (error && !category) {
    return (
      <div className="py-16">
        <div className="max-w-lg mx-auto text-center px-4">
          <h1 className="text-2xl font-bold text-foreground mb-4">Category Not Found</h1>
          <p className="text-muted-foreground mb-8">
            {error || "The category you're looking for doesn't exist."}
          </p>
          <Link
            to={oldStore.homePath}
            className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
          >
            Back to {oldStore.name}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb and Page Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link to={oldStore.homePath} className="hover:text-foreground">
              {oldStore.name}
            </Link>
            <span>/</span>
            <span className="text-foreground">{category?.name || 'Category'}</span>
          </div>

          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {category?.name || 'Loading...'}
              </h1>
              {category?.description && (
                <p className="text-muted-foreground mt-1">{category.description}</p>
              )}
            </div>
            <div className="w-full md:w-96">
              <SearchBar placeholder={`Search in ${category?.name || 'category'}...`} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-card rounded-lg p-4 shadow-sm border border-border">
              <h2 className="font-semibold text-foreground mb-4">Categories</h2>
              <CategoryNav categories={categories} currentSlug={slug} layout="vertical" />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-muted-foreground">
                {loading ? 'Loading...' : `${total} arche${total !== 1 ? 's' : ''}`}
              </p>

              <div className="flex items-center gap-2">
                <label htmlFor="sortBy" className="text-sm text-muted-foreground">
                  Sort by:
                </label>
                <select
                  id="sortBy"
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:ring-primary focus:border-primary"
                >
                  <option value="fitness">Quality</option>
                  <option value="popular">Most Popular</option>
                  <option value="newest">Newest</option>
                </select>
              </div>
            </div>

            {/* Results Grid */}
            <ArcheGrid arches={arches} loading={loading} />

            {/* Pagination */}
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </main>
        </div>
      </div>
    </div>
  );
}
